import { api } from "../base";
import type { IExecutionReport } from "@pulse-ui/codegen-v2/generated_ts/drop_copy";
import type {
  IMarkupTier,
  IMarkupTierSymbol,
} from "@pulse-ui/codegen-v2/generated_ts/markup_tier";
import type {
  IClstAccountTier,
  IClstAccountRiskBypass,
  IClstAccountAssetAllowlist,
  IClstAccountNotionalLimit,
  IClstAccountLpDesignation,
} from "@pulse-ui/codegen-v2/generated_ts/clst_account";
import type { IPulseApiResponse } from "@pulse-ui/codegen-v2/generated_ts/api";
import type {
  IBookSpotTradeRequest,
  IBulkBookJobAccepted,
  IBulkBookJobStatusResponse,
  IBulkBookSpotTradeRequest,
  IDealData,
  IFixExecutionReport,
  ITalosContractDealRequest,
  ITalosDealInfo,
  ITalosMultiDealLegResult,
  ITalosMultiDealResponse,
  ITradeData,
} from "@pulse-ui/codegen-v2/generated_ts/venues/talos";

// ── Types ───────────────────────────────────────────────────────────────────

export type {
  IMarkupTier,
  IMarkupTierSymbol,
  IClstAccountTier,
  IClstAccountRiskBypass,
  IClstAccountAssetAllowlist,
  IClstAccountNotionalLimit,
  IClstAccountLpDesignation,
  IBookSpotTradeRequest,
  IBulkBookJobAccepted,
  IBulkBookJobStatusResponse,
  IBulkBookSpotTradeRequest,
  IDealData,
  IFixExecutionReport,
  ITalosContractDealRequest,
  ITalosDealInfo,
  ITalosMultiDealLegResult,
  ITalosMultiDealResponse,
  ITradeData,
};

export {
  TalosMultiDealLegStatus,
  TalosOptionType,
  TalosPositionIndicator,
  TalosSide,
  BulkBookJobStatus,
  BulkBookRowStatus,
  type IBulkBookAssetDelta,
  type IBulkBookPreflightFailure,
  type IBulkBookPreflightResponse,
  type IBulkBookSpotTradeResultItem,
  type IBulkBookValidationFailure,
  type IBulkBookValidationResponse,
} from "@pulse-ui/codegen-v2/generated_ts/venues/talos";

// Async/optimistic ack carrying our exec_id: the trade is not yet booked and the
// confirmed fill lands in the blotter shortly. Distinct from IFixExecutionReport.
//
// `status` is optional because trading-ops-server does not actually send it --
// the 202 body is `{ "id": <exec_id> }` and nothing more (book_spot_trade.rs,
// the fills-booking cutover). The field is kept so a server that does send it
// still type-checks.
export interface IBookTradeAccepted {
  status?: "accepted";
  id: string;
}

export type BookSpotTradeResponse = IFixExecutionReport | IBookTradeAccepted;

/**
 * True when the response is the async 202 ack rather than a real execution
 * report.
 *
 * Matching on `status === "accepted"` alone silently misses every real 202:
 * the server sends only `{ id }`, so the ack fell through to the execution-
 * report branch and rendered a "Trade booked" card whose every field was
 * undefined. Discriminate on shape instead -- an ack carries `id` and none of
 * an ER's fields, and an ER carries `ExecID` and never `id`.
 */
export function isBookTradeAccepted(
  r: BookSpotTradeResponse,
): r is IBookTradeAccepted {
  const maybe = r as Partial<IBookTradeAccepted> & Partial<IFixExecutionReport>;
  if (maybe.status === "accepted") return true;
  return typeof maybe.id === "string" && maybe.ExecID === undefined;
}

export type TradeSide = "Buy" | "Sell";

export interface GetTradesParams {
  startDate?: string;
  endDate?: string;
  symbol?: string;
  markets?: string[];
  subAccounts?: string[];
  limit?: number;
}

export interface MarkupTierOption {
  code: string;
  display_name: string;
}

// ── BK Customer Trades (QuestDB) ───────────────────────────────────────────

export interface IBkTradeParty {
  party_role: string;
  party_id: string;
}

export interface IBkTradePayload {
  account?: string;
  order_id?: string;
  client_order_id?: string;
  exec_id?: string;
  exec_type?: string;
  symbol?: string;
  status?: string;
  side?: string;
  leaves_qty?: string;
  cum_qty?: string;
  cash_order_qty?: string;
  last_px?: string;
  last_qty?: string;
  parties?: IBkTradeParty[];
  handl_inst?: string;
  transact_time?: string;
  venue?: string;
  settl_date?: number;
  exec_ref_id?: string;
}

export interface IBkTrade {
  state: string;
  payload: IBkTradePayload;
  uuid: string;
  leg: string;
  client_trade_id: string | null;
}

export interface IBkTradesResponse {
  trades: IBkTrade[];
  limit: number;
  next_token?: string;
}

export interface GetBkTradesParams {
  venue?: string;
  account?: string;
  symbol?: string;
  leg?: string;
  side?: string;
  state?: string;
  counterparty?: string;
  exec_id?: string;
  bk_trade_id?: string;
  client_trade_id?: string;
  pair_complete?: boolean;
  from_ts?: string;
  to_ts?: string;
  uuid?: string;
  handl_inst?: string;
  entering_trader?: string;
  executing_system?: string;
  limit?: number;
  token?: string;
}

export interface CancelSpotTradeRequest {
  exec_id: string;
  symbol: string;
  side: string;
  account: string;
  leg: string;
  counterparty?: string;
}

export interface CancelSpotTradeResponse {
  id: string;
}

// ── Endpoints ───────────────────────────────────────────────────────────────

const tradeEndpoints = api.injectEndpoints({
  endpoints: (build) => ({
    // ── Markup Tiers ──────────────────────────────────────────────────

    /** Fetches all markup tiers. */
    getMarkupTiers: build.query<IMarkupTier[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates a markup tier. */
    createMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Updates a markup tier. */
    updateMarkupTier: build.mutation<void, Omit<IMarkupTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/markup-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["MarkupTiers"],
    }),

    /** Deletes a markup tier by code. */
    deleteMarkupTier: build.mutation<void, string>({
      query: (tierCode) => ({
        url: "/api/trade/v1/markup-tier",
        method: "DELETE",
        params: { tier_code: tierCode },
      }),
      invalidatesTags: ["MarkupTiers", "MarkupTierSymbols"],
    }),

    // ── Markup Tier Symbols (per-symbol overrides) ───────────────────

    /** Fetches per-symbol markup overrides, optionally filtered by tier_code and/or symbol. */
    getMarkupTierSymbols: build.query<
      IMarkupTierSymbol[],
      { tier_code?: string; symbol?: string } | void
    >({
      query: (params) => ({
        url: "/api/trade/v1/markup-tier-symbol",
        params: params ?? undefined,
      }),
      providesTags: ["MarkupTierSymbols"],
    }),

    /** Creates a per-symbol markup override. */
    createMarkupTierSymbol: build.mutation<
      IMarkupTierSymbol,
      Omit<IMarkupTierSymbol, "id">
    >({
      query: (body) => ({
        url: "/api/trade/v1/markup-tier-symbol",
        method: "POST",
        body,
      }),
      invalidatesTags: ["MarkupTierSymbols"],
    }),

    /** Updates a per-symbol markup override. */
    updateMarkupTierSymbol: build.mutation<
      IMarkupTierSymbol,
      Omit<IMarkupTierSymbol, "id">
    >({
      query: (body) => ({
        url: "/api/trade/v1/markup-tier-symbol",
        method: "PUT",
        body,
      }),
      invalidatesTags: ["MarkupTierSymbols"],
    }),

    /** Deletes a per-symbol markup override. */
    deleteMarkupTierSymbol: build.mutation<
      void,
      { tier_code: string; symbol: string }
    >({
      query: ({ tier_code, symbol }) => ({
        url: "/api/trade/v1/markup-tier-symbol",
        method: "DELETE",
        params: { tier_code, symbol },
      }),
      invalidatesTags: ["MarkupTierSymbols"],
    }),

    // ── Account Tiers ─────────────────────────────────────────────────

    /** Fetches all account-tier assignments. */
    getAccountTiers: build.query<IClstAccountTier[], void>({
      query: () => ({ url: "/api/trade/v1/clst-account-tier" }),
      providesTags: ["AccountTiers"],
    }),

    /** Fetches markup tier options (code + display_name) for account-tier dropdowns. */
    getMarkupTierOptions: build.query<MarkupTierOption[], void>({
      query: () => ({ url: "/api/trade/v1/markup-tier" }),
      transformResponse: (response: IMarkupTier[]) =>
        (response ?? []).map((t) => ({
          code: t.code,
          display_name: t.display_name,
        })),
      providesTags: ["MarkupTiers"],
    }),

    /** Creates an account-tier assignment. */
    createAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "POST",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Updates an account-tier assignment. */
    updateAccountTier: build.mutation<void, Omit<IClstAccountTier, "id">>({
      query: (tier) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "PUT",
        body: tier,
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    /** Deletes an account-tier assignment. */
    deleteAccountTier: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/clst-account-tier",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["AccountTiers"],
    }),

    // ── Risk Bypass Entitlements ────────────────────────────────────────

    /** Fetches all account risk-bypass entitlements. */
    getRiskBypassEntitlements: build.query<IClstAccountRiskBypass[], void>({
      query: () => ({ url: "/api/trade/v1/clst-account-risk-bypass" }),
      providesTags: ["RiskBypassEntitlements"],
    }),

    /** Creates an account risk-bypass entitlement. */
    createRiskBypassEntitlement: build.mutation<
      void,
      Omit<IClstAccountRiskBypass, "id">
    >({
      query: (entitlement) => ({
        url: "/api/trade/v1/clst-account-risk-bypass",
        method: "POST",
        body: entitlement,
      }),
      invalidatesTags: ["RiskBypassEntitlements"],
    }),

    /** Deletes an account risk-bypass entitlement. */
    deleteRiskBypassEntitlement: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/clst-account-risk-bypass",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["RiskBypassEntitlements"],
    }),

    // ── Counterparty Limits ─────────────────────────────────────────────

    /** Fetches all per-account asset allowlist entries. */
    getCounterpartyAssetAllowlist: build.query<
      IClstAccountAssetAllowlist[],
      void
    >({
      query: () => ({ url: "/api/trade/v1/counterparty-asset-allowlist" }),
      providesTags: ["CounterpartyAssetAllowlistEntries"],
    }),

    /** Creates an asset allowlist entry. */
    createCounterpartyAssetAllowlistEntry: build.mutation<
      void,
      Omit<IClstAccountAssetAllowlist, "id">
    >({
      query: (entry) => ({
        url: "/api/trade/v1/counterparty-asset-allowlist",
        method: "POST",
        body: entry,
      }),
      invalidatesTags: ["CounterpartyAssetAllowlistEntries"],
    }),

    /** Deletes an asset allowlist entry. */
    deleteCounterpartyAssetAllowlistEntry: build.mutation<
      void,
      Omit<IClstAccountAssetAllowlist, "id">
    >({
      query: ({ clst_account, asset }) => ({
        url: "/api/trade/v1/counterparty-asset-allowlist",
        method: "DELETE",
        params: { clst_account, asset },
      }),
      invalidatesTags: ["CounterpartyAssetAllowlistEntries"],
    }),

    /** Fetches all per-account notional limits. */
    getCounterpartyNotionalLimits: build.query<
      IClstAccountNotionalLimit[],
      void
    >({
      query: () => ({ url: "/api/trade/v1/counterparty-notional-limit" }),
      providesTags: ["CounterpartyNotionalLimits"],
    }),

    /** Creates a notional limit. */
    createCounterpartyNotionalLimit: build.mutation<
      void,
      Omit<IClstAccountNotionalLimit, "id">
    >({
      query: (limit) => ({
        url: "/api/trade/v1/counterparty-notional-limit",
        method: "POST",
        body: limit,
      }),
      invalidatesTags: ["CounterpartyNotionalLimits"],
    }),

    /** Updates a notional limit. */
    updateCounterpartyNotionalLimit: build.mutation<
      void,
      Omit<IClstAccountNotionalLimit, "id">
    >({
      query: (limit) => ({
        url: "/api/trade/v1/counterparty-notional-limit",
        method: "PUT",
        body: limit,
      }),
      invalidatesTags: ["CounterpartyNotionalLimits"],
    }),

    /** Deletes a notional limit by account. */
    deleteCounterpartyNotionalLimit: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/counterparty-notional-limit",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["CounterpartyNotionalLimits"],
    }),

    /** Fetches all LP-designated accounts. */
    getCounterpartyLpDesignations: build.query<
      IClstAccountLpDesignation[],
      void
    >({
      query: () => ({ url: "/api/trade/v1/counterparty-lp-designation" }),
      providesTags: ["CounterpartyLpDesignations"],
    }),

    /** Designates an account as an LP. */
    createCounterpartyLpDesignation: build.mutation<
      void,
      Omit<IClstAccountLpDesignation, "id">
    >({
      query: (designation) => ({
        url: "/api/trade/v1/counterparty-lp-designation",
        method: "POST",
        body: designation,
      }),
      invalidatesTags: ["CounterpartyLpDesignations"],
    }),

    /** Removes an account's LP designation. */
    deleteCounterpartyLpDesignation: build.mutation<void, string>({
      query: (clstAccount) => ({
        url: "/api/trade/v1/counterparty-lp-designation",
        method: "DELETE",
        params: { clst_account: clstAccount },
      }),
      invalidatesTags: ["CounterpartyLpDesignations"],
    }),

    /** Books one spot leg via trading-ops: the endpoint takes a single fully-formed
     *  ExecutionReport and publishes it as-sent. */
    bookSpotTrade: build.mutation<BookSpotTradeResponse, IExecutionReport>({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "spot" },
        body,
      }),
    }),

    /** Books an options trade via trading-ops. Carries one result entry per
     *  booked leg; a 502 returns the same shape with per-leg failure detail. */
    bookOptionsTrade: build.mutation<
      ITalosMultiDealResponse,
      ITalosContractDealRequest
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trade",
        method: "POST",
        params: { asset_type: "options" },
        body,
      }),
    }),

    /** Cancels a customer trade by trade ID. */
    cancelCustomerTrade: build.mutation<IPulseApiResponse, string>({
      query: (tradeId) => ({
        url: `/api/trading-ops/v1/customer-trades/${tradeId}`,
        method: "DELETE",
      }),
    }),

    /** Cancels (closes) an options contract deal by deal ID. */
    cancelContractDeal: build.mutation<IPulseApiResponse, string>({
      query: (dealId) => ({
        url: `/api/trading-ops/v1/deals/${dealId}`,
        method: "DELETE",
      }),
    }),

    /**
     * Submits a bulk-book batch. Returns 202 + a job handle once the batch
     * passes synchronous validation and aggregate credit pre-flight; poll
     * `getBulkJobStatus` for execution progress. A 400 carries either a
     * `IBulkBookValidationResponse` or `IBulkBookPreflightResponse` in `error.data`.
     */
    bulkBookTrades: build.mutation<
      IBulkBookJobAccepted,
      IBulkBookSpotTradeRequest
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/book-trades/bulk",
        method: "POST",
        body,
      }),
    }),

    /** Polls the status of an async bulk-book job. */
    getBulkJobStatus: build.query<IBulkBookJobStatusResponse, string>({
      query: (jobId) => ({
        url: `/api/trading-ops/v1/bulk-jobs/${jobId}`,
      }),
    }),

    /** Fetches recent trades from trading-ops-server. */
    getTrades: build.query<ITradeData[], GetTradesParams>({
      query: ({ startDate, endDate, symbol, markets, subAccounts, limit }) => ({
        url: "/api/trading-ops/v1/trades",
        params: {
          ...(startDate && { start_date: startDate }),
          ...(endDate && { end_date: endDate }),
          ...(symbol && { symbol }),
          ...(markets && markets.length > 0 && { markets: markets.join(",") }),
          ...(subAccounts &&
            subAccounts.length > 0 && { sub_accounts: subAccounts.join(",") }),
          ...(limit !== undefined && { limit }),
        },
      }),
      transformResponse: (response: ITradeData[] | { data: ITradeData[] }) =>
        Array.isArray(response) ? response : (response.data ?? []),
      providesTags: ["Trades"],
    }),

    /** Fetches BK customer trades from QuestDB. */
    getBkTrades: build.query<IBkTradesResponse, GetBkTradesParams>({
      query: (params) => ({
        url: "/api/trading-ops/v1/trades",
        params: Object.fromEntries(
          Object.entries(params).filter(([, v]) => v !== undefined && v !== ""),
        ),
      }),
      providesTags: ["BkTrades"],
    }),

    /** Cancels a spot trade via the BK cancel pipeline. */
    cancelSpotTrade: build.mutation<
      CancelSpotTradeResponse,
      CancelSpotTradeRequest
    >({
      query: (body) => ({
        url: "/api/trading-ops/v1/cancel-spot-trade",
        method: "POST",
        body,
      }),
      invalidatesTags: ["BkTrades"],
    }),
  }),
});

export const {
  useGetMarkupTiersQuery,
  useCreateMarkupTierMutation,
  useUpdateMarkupTierMutation,
  useDeleteMarkupTierMutation,
  useGetMarkupTierSymbolsQuery,
  useCreateMarkupTierSymbolMutation,
  useUpdateMarkupTierSymbolMutation,
  useDeleteMarkupTierSymbolMutation,
  useGetAccountTiersQuery,
  useGetMarkupTierOptionsQuery,
  useCreateAccountTierMutation,
  useUpdateAccountTierMutation,
  useDeleteAccountTierMutation,
  useGetRiskBypassEntitlementsQuery,
  useCreateRiskBypassEntitlementMutation,
  useDeleteRiskBypassEntitlementMutation,
  useGetCounterpartyAssetAllowlistQuery,
  useCreateCounterpartyAssetAllowlistEntryMutation,
  useDeleteCounterpartyAssetAllowlistEntryMutation,
  useGetCounterpartyNotionalLimitsQuery,
  useCreateCounterpartyNotionalLimitMutation,
  useUpdateCounterpartyNotionalLimitMutation,
  useDeleteCounterpartyNotionalLimitMutation,
  useGetCounterpartyLpDesignationsQuery,
  useCreateCounterpartyLpDesignationMutation,
  useDeleteCounterpartyLpDesignationMutation,
  useCancelCustomerTradeMutation,
  useCancelContractDealMutation,
  useBookSpotTradeMutation,
  useBookOptionsTradeMutation,
  useBulkBookTradesMutation,
  useGetBulkJobStatusQuery,
  useGetTradesQuery,
  useLazyGetTradesQuery,
  useGetBkTradesQuery,
  useLazyGetBkTradesQuery,
  useCancelSpotTradeMutation,
} = tradeEndpoints;
